import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Download as DownloadIcon,
  PlayArrow as ProcessIcon,
  History as HistoryIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { documentsApi } from '../../services/documentsApi';
import { 
  Document, 
  DocumentStatus, 
  DocumentCategory, 
  WorkflowStage,
  DocumentHistoryResponse 
} from '../../types/documents';

interface DocumentListProps {
  clientId?: string;
}

export const DocumentList: React.FC<DocumentListProps> = ({ clientId }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<(Document & DocumentHistoryResponse) | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '' as DocumentStatus | '',
    documentType: '',
    category: '' as DocumentCategory | '',
    stage: '' as WorkflowStage | ''
  });

  useEffect(() => {
    loadDocuments();
  }, [clientId, filters]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (clientId) params.clientId = clientId;
      if (filters.status) params.status = filters.status;
      if (filters.documentType) params.documentType = filters.documentType;
      if (filters.category) params.category = filters.category;
      if (filters.stage) params.stage = filters.stage;

      const response = await documentsApi.getDocuments(params);
      setDocuments(response.documents);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDocument = async (documentId: string) => {
    try {
      await documentsApi.processDocument(documentId);
      await loadDocuments(); // Refresh list
    } catch (error) {
      console.error('Failed to process document:', error);
    }
  };

  const handleDownload = async (documentId: string) => {
    try {
      const response = await documentsApi.getDownloadUrl(documentId);
      window.open(response.downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const showHistory = async (document: Document) => {
    try {
      const history = await documentsApi.getDocumentHistory(document.id);
      setSelectedDocument({ ...document, ...history });
      setHistoryOpen(true);
    } catch (error) {
      console.error('Failed to load document history:', error);
    }
  };

  const getStatusColor = (status: DocumentStatus) => {
    const colors: Record<DocumentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'uploaded': 'info',
      'processing': 'warning', 
      'processed': 'success',
      'categorized': 'success',
      'pending_signature': 'warning',
      'signed': 'success',
      'archived': 'default',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  const getStageColor = (stage: WorkflowStage) => {
    const colors: Record<WorkflowStage, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'upload': 'success',
      'ocr': 'info',
      'categorization': 'info',
      'signature': 'warning',
      'complete': 'success'
    };
    return colors[stage] || 'default';
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterIcon sx={{ mr: 1 }} />
            Filters
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as DocumentStatus }))}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="uploaded">Uploaded</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
                <MenuItem value="categorized">Categorized</MenuItem>
                <MenuItem value="pending_signature">Pending Signature</MenuItem>
                <MenuItem value="signed">Signed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value as DocumentCategory }))}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="financial">Financial</MenuItem>
                <MenuItem value="legal">Legal</MenuItem>
                <MenuItem value="identity">Identity</MenuItem>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="compliance">Compliance</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Stage</InputLabel>
              <Select
                value={filters.stage}
                onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value as WorkflowStage }))}
                label="Stage"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="upload">Upload</MenuItem>
                <MenuItem value="ocr">OCR</MenuItem>
                <MenuItem value="categorization">Categorization</MenuItem>
                <MenuItem value="signature">Signature</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Documents ({documents.length})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {document.fileName}
                      </Typography>
                      {document.classification && (
                        <Typography variant="caption" color="text.secondary">
                          Confidence: {(document.classification.confidence && (document.classification.confidence * 100).toFixed(0))}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{document.documentType.replace('_', ' ')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={document.category} 
                        size="small" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>{formatFileSize(document.fileSize)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={document.status.replace('_', ' ')} 
                        color={getStatusColor(document.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={document.workflow.stage} 
                        color={getStageColor(document.workflow.stage)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(document.createdAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(document.id)}
                          title="Download"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => showHistory(document)}
                          title="View History"
                        >
                          <HistoryIcon />
                        </IconButton>
                        {document.status !== 'processing' && document.workflow.stage !== 'complete' && (
                          <IconButton
                            size="small"
                            onClick={() => handleProcessDocument(document.id)}
                            title="Process Document"
                          >
                            <ProcessIcon />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {documents.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No documents found. Upload some documents to get started.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Processing History</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedDocument.fileName}
              </Typography>
              
              {selectedDocument.ocrData && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    OCR Results (Confidence: {selectedDocument.ocrData.confidence ? (selectedDocument.ocrData.confidence * 100).toFixed(0) : 'N/A'}%)
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={(selectedDocument.ocrData.extractedText || '').substring(0, 500) + '...'}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Processing Timeline
              </Typography>
              <Timeline>
                {selectedDocument.workflowHistory?.map((stage, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot color={stage.status === 'completed' ? 'success' : stage.status === 'failed' ? 'error' : 'primary'} />
                      {index < (selectedDocument.workflowHistory?.length || 0) - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="medium">
                        {stage.stage.toUpperCase()} - {stage.status}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(stage.timestamp)}
                        {stage.duration && ` (${stage.duration}ms)`}
                      </Typography>
                      {stage.error && (
                        <Typography variant="body2" color="error">
                          Error: {stage.error}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentList;